// =============================================================================
// FILE: packages/renderer/src/lib/hardwareId.ts
// PURPOSE: Hardware-Locked Licensing (renderer side). Produces a stable machine
//   fingerprint (HWID) and drives the launch-time verification against the
//   backend. In Electron, prefers a main-process HWID bridge (CPU/board serial);
//   otherwise falls back to a persisted device fingerprint. Inert in web preview
//   unless a Legacy license key has been stored on this device.
// =============================================================================

const HWID_KEY = 'rcd2_hwid';
export const LEGACY_KEY_STORAGE = 'rcd2_legacy_key';
const backendUrl = (import.meta.env.VITE_BACKEND_URL as string) ?? '';

function djb2(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

/** Stable HWID for this machine. Prefers the Electron main-process bridge. */
export async function getHardwareId(): Promise<string> {
  const bridge = (window as unknown as { api?: { getHardwareId?: () => Promise<string> } }).api?.getHardwareId;
  if (typeof bridge === 'function') {
    try { const id = await bridge(); if (id) return String(id); } catch { /* fall through */ }
  }
  let id = localStorage.getItem(HWID_KEY);
  if (!id) {
    const seed = [
      navigator.userAgent, navigator.language, String(navigator.hardwareConcurrency ?? ''),
      `${screen.width}x${screen.height}x${screen.colorDepth}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
    ].join('|');
    id = `HW-${djb2(seed)}-${djb2(seed.split('').reverse().join(''))}`;
    localStorage.setItem(HWID_KEY, id);
  }
  return id;
}

export function getStoredLegacyKey(): string | null {
  return localStorage.getItem(LEGACY_KEY_STORAGE);
}
export function setStoredLegacyKey(key: string): void {
  localStorage.setItem(LEGACY_KEY_STORAGE, key);
}

export interface HwidVerdict { valid: boolean; locked?: boolean; bound?: boolean; message?: string }

/** Bind the license to this machine (first activation). */
export async function bindLicenseToDevice(licenseKey: string): Promise<HwidVerdict> {
  const hwid = await getHardwareId();
  const res = await fetch(`${backendUrl}/api/license/bind-hwid`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ license_key: licenseKey, hwid }),
  });
  return res.json() as Promise<HwidVerdict>;
}

/** Verify this machine still owns the license (called on launch). */
export async function verifyDeviceLicense(licenseKey: string): Promise<HwidVerdict> {
  const hwid = await getHardwareId();
  const res = await fetch(`${backendUrl}/api/license/verify-hwid`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ license_key: licenseKey, hwid }),
  });
  return res.json() as Promise<HwidVerdict>;
}
